import { CoreEvent, eventNames } from '@carvana/core-events';
import { Clickable } from '@carvana/merch-components';
import type { InventoryVehicle } from '@carvana/merch-contracts';
import { DispatcherEvent, NavPayloadAction } from '@carvana/merch-native';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { Fragment, memo, useContext, useState } from 'react';
import AnchorButton from '@/common/AnchorButton';
import Sections from '~/constants/home/sections';
import { VdpCardType } from '~/constants/home/VdpCardType';
import { useIsNative } from '~/context/common/DeviceContext';
import GlobalContext from '~/context/home/GlobalContext';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import formatEvent from '~/utilities/formatEvent';
import useGetVDPLinksToShow, { VDPLinkTitles } from './useGetVDPLinksToShow';
import VehicleLinkCard from './VehicleLinkCard';
import {
  DividerLine,
  Header,
  HeaderWrapper,
  LinkCardTrackWrapper,
  LinkCardsContainer
} from '../LinkCards.styles';
import LinksLoadingCard, { LinkCardType } from '../LinksLoadingCard';

function getLinkCardType(header: VDPLinkTitles): VdpCardType {
  switch (header) {
    case VDPLinkTitles.Favorite:
      return VdpCardType.Favorite;
    case VDPLinkTitles.Recent:
      return VdpCardType.Recent;
    case VDPLinkTitles.Suggested:
      return VdpCardType.Suggested;
    default:
      return VdpCardType.Suggested;
  }
}

function VDPLinks() {
  const {
    header,
    vehicles,
    showLink = false,
    showLoading
  } = useGetVDPLinksToShow();
  const { setIsVdpLinksLoaded, setVdpCardTypeShown } =
    useContext(GlobalContext);
  const [vehiclesToShow, setVehiclesToShow] = useState<InventoryVehicle[]>([]);
  const [hasLink, setHasLink] = useState(false);
  const [headerToShow, setHeaderToShow] = useState(VDPLinkTitles.None);
  const [isLoaded, setIsLoaded] = useState(false);

  const isNative = useIsNative();

  //So that we don't swap vehicles when a user favorites a vehicle
  useEffectOnce(
    () => {
      setVehiclesToShow(vehicles);
      setHasLink(showLink);
      setHeaderToShow(header);
      setIsLoaded(true);
      setIsVdpLinksLoaded(true);
      setVdpCardTypeShown(getLinkCardType(header));
    },
    [header, setIsVdpLinksLoaded, setVdpCardTypeShown, showLink, vehicles],
    !showLoading
  );

  return isLoaded ? (
    <LinkCardTrackWrapper
      event={formatEvent('Home feed card', {
        action: 'viewed',
        description: 'VDP card',
        type: getLinkCardType(headerToShow)
      })}
      trackOnce
      data-testid="vdp-links-section"
    >
      <HeaderWrapper>
        <Header data-testid="vdp-links-header">{headerToShow}</Header>
        {hasLink && (
          <Clickable
            element={AnchorButton}
            className="text-text-primary t-label-sm"
            data-analytics-id="Home feed card"
            data-analytics-attributes={{
              description: 'VDP card',
              type: 'favorites',
              item: 'view all'
            }}
            data-testid="vdp-links-view-all"
            onClick={() => {
              if (isNative) {
                sendMessageToNative(DispatcherEvent.Link, {
                  action: NavPayloadAction.ViewFavorites
                });
              } else {
                CoreEvent.emit(eventNames.favorites.mfe.drawer.open, {
                  tab: 'vehicles'
                });
              }
            }}
          >
            View All
          </Clickable>
        )}
      </HeaderWrapper>
      <LinkCardsContainer data-testid="vdp-links-container">
        {vehiclesToShow.map((vehicle, index) => (
          <Fragment key={vehicle.vehicleId}>
            {index !== 0 && <DividerLine />}
            <VehicleLinkCard
              type={getLinkCardType(headerToShow)}
              vehicle={vehicle}
            />
          </Fragment>
        ))}
      </LinkCardsContainer>
    </LinkCardTrackWrapper>
  ) : (
    <LinksLoadingCard card={LinkCardType.VDP} />
  );
}

export default withErrorBoundary(memo(VDPLinks), {
  section: Sections.VDPLinks
});
